import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FC
} from 'react';
import { createPortal } from 'react-dom';
import { SearchIcon, SparklesIcon } from 'lucide-react';
import { useAui, useAuiState } from '@assistant-ui/react';
import { cn } from '@/lib/utils';
import {
  MCP_SLASH_MODE_SKILLS,
  type McpSlashSkill
} from '../config/mcpSlashSkills';
import { useListMcpChatSkillsQuery } from '../api/mcpApi';
import type { McpChatSkill } from '../types/mcpApiTypes';
import { buildSkillUserMessage } from '../utils/skillMarkers';
import { useOptionalMcpHelpMode } from './McpHelpModeContext';

/** Match a trailing slash-command token: `/` or `/query` (no spaces in query). */
const SLASH_TOKEN_RE = /(^|[\s\u200b])\/([^\s]*)$/;

/** Gap between composer and menu, and between menu and thread top. */
const MENU_GAP_PX = 8;
const MENU_MAX_HEIGHT_PX = 280;
const MENU_MIN_HEIGHT_PX = 140;

type SlashMatch = {
  query: string;
  /** Start index of `/` in the full composer text. */
  slashIndex: number;
};

function parseSlashMatch(text: string): SlashMatch | null {
  const match = text.match(SLASH_TOKEN_RE);
  if (!match || match.index == null) return null;
  const prefix = match[1] ?? '';
  return {
    query: match[2] ?? '',
    slashIndex: match.index + prefix.length
  };
}

function toSlashSkill(skill: McpChatSkill): McpSlashSkill {
  return {
    id: skill.id,
    command: skill.command,
    title: skill.title,
    description: skill.shortDescription || skill.description,
    category: skill.category,
    prompt: skill.composerHint ?? '',
    source: 'api'
  };
}

function filterSkills(skills: McpSlashSkill[], query: string): McpSlashSkill[] {
  const q = query.trim().toLowerCase();
  if (!q) return skills;
  return skills.filter(skill => {
    const hay = [
      skill.command,
      skill.title,
      skill.description,
      skill.category
    ]
      .join(' ')
      .toLowerCase();
    return hay.includes(q) || skill.command.startsWith(q);
  });
}

function appendThreadMessage(aui: ReturnType<typeof useAui>, text: string) {
  try {
    const thread =
      typeof (aui as { thread?: unknown }).thread === 'function'
        ? (
            aui as {
              thread: () => { append: (message: string) => void };
            }
          ).thread()
        : (
            aui as {
              thread: { append: (message: string) => void };
            }
          ).thread;
    thread.append(text);
  } catch (error) {
    console.error('Failed to append thread message for skill', error);
  }
}

function setComposerText(
  aui: ReturnType<typeof useAui>,
  text: string
): void {
  try {
    const composer =
      typeof (aui as { composer?: unknown }).composer === 'function'
        ? (
            aui as {
              composer: () => { setText: (value: string) => void };
            }
          ).composer()
        : (
            aui as {
              composer: { setText: (value: string) => void };
            }
          ).composer;
    composer.setText(text);
    window.requestAnimationFrame(() => {
      const input = document.querySelector<HTMLTextAreaElement>(
        '.aui-composer-input, textarea[aria-label="Message input"]'
      );
      input?.focus();
      const cursor = text.length;
      input?.setSelectionRange(cursor, cursor);
    });
  } catch (error) {
    console.error('Failed to set composer text from slash skill', error);
  }
}

type MenuLayout = {
  left: number;
  width: number;
  bottom: number;
  maxHeight: number;
};

/**
 * ChatGPT-style `/` command menu for chat skills.
 * Always opens upward above the composer. Rendered in a portal on the chat
 * card so the scrolling thread viewport cannot clip its top and it may
 * extend over the chat header.
 */
export const McpSlashCommandMenu: FC = () => {
  const aui = useAui();
  const helpMode = useOptionalMcpHelpMode();
  const text = useAuiState(s => s.composer.text);
  const match = useMemo(() => parseSlashMatch(text), [text]);
  const { data: skillsData } = useListMcpChatSkillsQuery();

  const allSkills = useMemo(() => {
    const apiSkills = (skillsData?.items ?? []).map(toSlashSkill);
    return [...MCP_SLASH_MODE_SKILLS, ...apiSkills];
  }, [skillsData?.items]);

  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const searchFocusedRef = useRef(false);
  const wasOpenRef = useRef(false);

  const menuOpen = Boolean(match);

  // Seed search from `/query` while the search box is not focused.
  useEffect(() => {
    if (!match) {
      setSearch('');
      wasOpenRef.current = false;
      return;
    }
    if (!searchFocusedRef.current) {
      setSearch(match.query);
    }
    if (!wasOpenRef.current) {
      wasOpenRef.current = true;
      window.requestAnimationFrame(() => {
        searchRef.current?.focus();
        searchRef.current?.select();
      });
    }
  }, [match]);

  const items = useMemo(
    () => filterSkills(allSkills, search),
    [allSkills, search]
  );
  const empty = menuOpen && items.length === 0;

  // Position the portaled menu above the composer, inside the chat card.
  const probeRef = useRef<HTMLSpanElement>(null);
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);
  const [layout, setLayout] = useState<MenuLayout | null>(null);

  useLayoutEffect(() => {
    if (!menuOpen) {
      setLayout(null);
      return;
    }
    const probe = probeRef.current;
    const anchor = probe?.parentElement;
    // Chat card (includes the header) so the menu may extend over it.
    const host =
      probe?.closest<HTMLElement>('.mcp-chat-page') ??
      probe?.closest<HTMLElement>('.aui-thread-root');
    if (!anchor || !host) return;
    setPortalHost(host);

    const update = () => {
      const a = anchor.getBoundingClientRect();
      const h = host.getBoundingClientRect();
      const next: MenuLayout = {
        left: Math.round(a.left - h.left),
        width: Math.round(a.width),
        bottom: Math.round(h.bottom - a.top + MENU_GAP_PX),
        maxHeight: Math.round(
          Math.min(
            MENU_MAX_HEIGHT_PX,
            Math.max(MENU_MIN_HEIGHT_PX, a.top - h.top - MENU_GAP_PX * 2)
          )
        )
      };
      setLayout(prev =>
        prev &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.bottom === next.bottom &&
        prev.maxHeight === next.maxHeight
          ? prev
          : next
      );
    };

    update();
    window.addEventListener('resize', update);
    // Capture scrolls from the thread viewport (the composer moves with it).
    host.addEventListener('scroll', update, true);
    const observer = new ResizeObserver(update);
    observer.observe(anchor);
    observer.observe(host);
    return () => {
      window.removeEventListener('resize', update);
      host.removeEventListener('scroll', update, true);
      observer.disconnect();
    };
  }, [menuOpen]);

  useEffect(() => {
    setActiveIndex(0);
  }, [search, menuOpen]);

  useEffect(() => {
    if (!menuOpen || items.length === 0) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-slash-index="${activeIndex}"]`
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, menuOpen, items.length]);

  const selectSkill = useCallback(
    (skill: McpSlashSkill) => {
      if (!match) return;
      if (skill.action === 'enter-help') {
        setComposerText(aui, text.slice(0, match.slashIndex).replace(/\s+$/, ''));
        helpMode?.enterHelpMode();
        return;
      }
      if (skill.source === 'api') {
        setComposerText(aui, text.slice(0, match.slashIndex).replace(/\s+$/, ''));
        appendThreadMessage(
          aui,
          buildSkillUserMessage({
            command: skill.command,
            title: skill.title
          })
        );
        return;
      }
      const before = text.slice(0, match.slashIndex);
      const nextText =
        skill.prompt && skill.prompt.trim().length > 0
          ? `${before}${skill.prompt}`
          : before.replace(/\s+$/, '');
      setComposerText(aui, nextText);
    },
    [aui, helpMode, match, text]
  );

  const dismissSlash = useCallback(() => {
    if (!match) return;
    setComposerText(aui, text.slice(0, match.slashIndex).replace(/\s+$/, ''));
  }, [aui, match, text]);

  // Keyboard: works from search input or composer.
  useEffect(() => {
    if (!menuOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as Node | null;
      const inMenu = Boolean(
        target && listRef.current?.contains(target)
      );
      const inComposer = Boolean(
        target &&
          (target as HTMLElement).closest?.(
            '.aui-composer-root, .aui-composer-input'
          )
      );
      if (!inMenu && !inComposer) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        dismissSlash();
        return;
      }

      if (items.length === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        setActiveIndex(i => (i + 1) % items.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        setActiveIndex(i => (i - 1 + items.length) % items.length);
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        const skill = items[activeIndex];
        if (skill) selectSkill(skill);
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [menuOpen, items, activeIndex, selectSkill, dismissSlash]);

  if (!menuOpen) return null;

  const menu = (
    <div
      ref={listRef}
      role="listbox"
      aria-label="دستورات چت"
      dir="rtl"
      style={
        layout
          ? {
              left: layout.left,
              width: layout.width,
              bottom: layout.bottom,
              maxHeight: layout.maxHeight
            }
          : { visibility: 'hidden' }
      }
      className="!bg-popover !text-popover-foreground !border-border absolute z-30 flex h-fit flex-col overflow-hidden rounded-2xl !border shadow-lg !opacity-100 !backdrop-blur-none"
    >
      <div className="border-border/70 flex h-12 shrink-0 items-center gap-2 border-b px-2.5">
        <div className="text-muted-foreground flex h-8 shrink-0 items-center gap-1.5">
          <SparklesIcon className="size-3.5 opacity-70" aria-hidden />
          <span className="text-xs leading-none font-semibold whitespace-nowrap">
            دستورات
          </span>
        </div>

        <label className="bg-muted/40 focus-within:bg-muted/60 ms-auto flex h-8 w-full max-w-[10.5rem] shrink-0 items-center gap-1.5 overflow-hidden rounded-lg px-2.5 transition-colors">
          <SearchIcon
            className="text-muted-foreground size-3 shrink-0 opacity-70"
            aria-hidden
          />
          <input
            ref={searchRef}
            type="text"
            value={search}
            placeholder="جستجو..."
            aria-label="جستجوی مهارت"
            autoComplete="off"
            spellCheck={false}
            className="text-foreground placeholder:text-muted-foreground !m-0 !h-auto !w-auto min-w-0 flex-1 !bg-transparent !p-0 text-xs leading-none outline-none"
            onFocus={() => {
              searchFocusedRef.current = true;
            }}
            onBlur={() => {
              searchFocusedRef.current = false;
            }}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => {
              if (
                e.key === 'ArrowDown' ||
                e.key === 'ArrowUp' ||
                e.key === 'Enter' ||
                e.key === 'Escape'
              ) {
                return;
              }
              e.stopPropagation();
            }}
          />
        </label>
      </div>

      {empty ? (
        <div className="text-muted-foreground flex items-center gap-2 px-3 py-3 text-sm">
          <SearchIcon className="size-4 opacity-50" aria-hidden />
          <span>مهارتی پیدا نشد</span>
        </div>
      ) : (
        <ul className="m-0 min-h-0 list-none overflow-y-auto p-1">
          {items.map((skill, index) => {
            const active = index === activeIndex;
            return (
              <li key={skill.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  data-slash-index={index}
                  className={cn(
                    'text-foreground flex h-auto w-full cursor-pointer items-start justify-between gap-3 rounded-xl px-2.5 py-1.5 text-start transition-colors',
                    '!border-0 !bg-transparent !shadow-none',
                    'hover:!bg-muted/60',
                    active && '!bg-muted hover:!bg-muted'
                  )}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={e => {
                    e.preventDefault();
                  }}
                  onClick={() => selectSkill(skill)}
                >
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-xs leading-snug">
                      {skill.title}
                    </span>
                    <span className="text-muted-foreground text-[0.7rem] leading-snug">
                      {skill.description}
                    </span>
                  </span>
                  <span
                    className="text-muted-foreground shrink-0 font-mono text-[0.7rem] opacity-90"
                    dir="ltr"
                  >
                    /{skill.command}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="text-muted-foreground border-border/70 flex shrink-0 flex-wrap gap-3 border-t px-3 py-1.5 text-[0.68rem] opacity-85">
        <span>↑↓ حرکت</span>
        <span>Enter انتخاب</span>
        <span>Esc بستن</span>
      </div>
    </div>
  );

  return (
    <>
      <span ref={probeRef} hidden />
      {portalHost ? createPortal(menu, portalHost) : null}
    </>
  );
};
